<template lang="pug">
    .floor-plan-fixture(
        :class="fixtureClasses"
        :style="fixtureStyle"
        @pointerdown="onPointerDown"
        @click="onClick"
    )
        .floor-plan-fixture__body
            svg.floor-plan-fixture__shape(v-if="isTriangle" viewBox="0 0 100 100" preserveAspectRatio="none")
                polygon(points="50,1 99,99 1,99" vector-effect="non-scaling-stroke")

            svg.floor-plan-fixture__shape(v-else-if="isStraightStairs" viewBox="0 0 100 100" preserveAspectRatio="none")
                rect(x="1" y="1" width="98" height="98" vector-effect="non-scaling-stroke")
                line(
                    v-for="step in stairSteps"
                    :key="'step-' + step"
                    x1="1"
                    :y1="step"
                    x2="99"
                    :y2="step"
                    vector-effect="non-scaling-stroke"
                )

            svg.floor-plan-fixture__shape(v-else-if="isSpiralStairs" viewBox="0 0 100 100" preserveAspectRatio="none")
                circle(cx="50" cy="50" r="48" vector-effect="non-scaling-stroke")
                line(
                    v-for="spoke in spiralSpokes"
                    :key="'spoke-' + spoke.key"
                    :x1="spoke.x1"
                    :y1="spoke.y1"
                    :x2="spoke.x2"
                    :y2="spoke.y2"
                    vector-effect="non-scaling-stroke"
                )
                circle(cx="50" cy="50" r="12" vector-effect="non-scaling-stroke")

            svg.floor-plan-fixture__direction(v-if="isStairs" viewBox="0 0 100 100" preserveAspectRatio="none")
                line(
                    x1="50"
                    :y1="directionArrow.y1"
                    x2="50"
                    :y2="directionArrow.y2"
                    vector-effect="non-scaling-stroke"
                )
                polygon(
                    v-for="(head, index) in directionArrow.heads"
                    :key="'head-' + index"
                    :points="head"
                    vector-effect="non-scaling-stroke"
                )

            .floor-plan-fixture__label(v-if="displayLabel") {{ displayLabel }}

        template(v-if="showHandles")
            .floor-plan-fixture__handle(
                v-for="handle in handles"
                :key="handle.key"
                :class="'floor-plan-fixture__handle--' + handle.key"
                :style="handle.style"
                @pointerdown.stop="startResize(handle.key, $event)"
            )
</template>

<script>
import _ from 'lodash';

const GRID_SIZE = 10;
const MINIMUM_SIZE = 20;
const MINIMUM_LINE_LENGTH = 40;
const ANGLE_SNAP_DEGREES = 15;
const STAIR_STEPS = 7;
const SPIRAL_SPOKES = 12;
const CLICK_THRESHOLD = 4;
const DOUBLE_TAP_MILLISECONDS = 350;

function toDegrees(radians) {
    return radians * 180 / Math.PI;
}

function toRadians(degrees) {
    return degrees * Math.PI / 180;
}

function normalizeDegrees(degrees) {
    return ((Math.round(degrees) % 360) + 360) % 360;
}

function snapToGrid(value) {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

export default {
    name: 'FloorPlanFixture',
    props: {
        fixture: {
            type: Object,
            required: true
        },
        selected: {
            type: Boolean,
            default: false
        },
        editMode: {
            type: Boolean,
            default: false
        },
        zoom: {
            type: Number,
            default: 1
        },
        targetRoomName: {
            type: String,
            default: ''
        }
    },
    data: function () {
        return {
            draft: null,
            gestureState: null,
            lastTapAt: 0
        };
    },
    computed: {
        // While dragging or resizing the draft renders, afterwards the stored fixture again
        geometry: function () {
            return this.draft || this.fixture;
        },
        fixtureType: function () {
            return this.geometry.type || 'rectangle';
        },
        isLine: function () {
            return this.fixtureType === 'line';
        },
        isTriangle: function () {
            return this.fixtureType === 'triangle';
        },
        isStraightStairs: function () {
            return this.fixtureType === 'stairs';
        },
        isSpiralStairs: function () {
            return this.fixtureType === 'spiral-stairs';
        },
        isStairs: function () {
            return this.isStraightStairs || this.isSpiralStairs;
        },
        isAreaLink: function () {
            return this.fixtureType === 'area-link';
        },
        hasTargetRoom: function () {
            return this.isStairs || this.isAreaLink;
        },
        // In view mode stairs and area links lead to the rooms they point at
        isNavigable: function () {
            return this.hasTargetRoom
                && !this.editMode
                && !!(this.fixture.targetRoomId || this.fixture.targetRoomDownId);
        },
        displayLabel: function () {
            if (this.geometry.label) {
                return this.geometry.label;
            }

            return this.hasTargetRoom ? this.targetRoomName : '';
        },
        directionArrow: function () {
            const upHead = '50,10 42,26 58,26';
            const downHead = '50,90 42,74 58,74';

            if (this.geometry.direction === 'both') {
                return { y1: 26, y2: 74, heads: [upHead, downHead] };
            }

            if (this.geometry.direction === 'down') {
                return { y1: 22, y2: 78, heads: [downHead] };
            }

            return { y1: 78, y2: 22, heads: [upHead] };
        },
        stairSteps: function () {
            const steps = [];

            for (let index = 1; index < STAIR_STEPS; index += 1) {
                steps.push(Math.round(index * 100 / STAIR_STEPS));
            }

            return steps;
        },
        spiralSpokes: function () {
            const spokes = [];

            for (let index = 0; index < SPIRAL_SPOKES; index += 1) {
                const angle = toRadians(index * 360 / SPIRAL_SPOKES);

                spokes.push({
                    key: index,
                    x1: 50 + Math.cos(angle) * 12,
                    y1: 50 + Math.sin(angle) * 12,
                    x2: 50 + Math.cos(angle) * 48,
                    y2: 50 + Math.sin(angle) * 48
                });
            }

            return spokes;
        },
        showHandles: function () {
            return this.editMode && this.selected;
        },
        fixtureClasses: function () {
            return [
                'floor-plan-fixture--' + this.fixtureType,
                {
                    'floor-plan-fixture--selected': this.selected && this.editMode,
                    'floor-plan-fixture--editable': this.editMode,
                    'floor-plan-fixture--navigable': this.isNavigable,
                    'floor-plan-fixture--busy': !!this.gestureState
                }
            ];
        },
        fixtureStyle: function () {
            return {
                left: this.geometry.x + 'px',
                top: this.geometry.y + 'px',
                width: this.geometry.width + 'px',
                height: this.geometry.height + 'px',
                transform: this.geometry.rotation ? 'rotate(' + this.geometry.rotation + 'deg)' : 'none'
            };
        },
        handles: function () {
            if (this.isLine) {
                return [
                    { key: 'start', style: { left: '0px', top: '50%' } },
                    { key: 'end', style: { left: '100%', top: '50%' } }
                ];
            }

            const handles = [
                { key: 'nw', style: { left: '0px', top: '0px' } },
                { key: 'ne', style: { left: '100%', top: '0px' } },
                { key: 'se', style: { left: '100%', top: '100%' } },
                { key: 'sw', style: { left: '0px', top: '100%' } }
            ];

            if (this.fixtureType !== 'circle') {
                handles.push({ key: 'rotate', style: { left: '50%', top: '-22px' } });
            }

            return handles;
        }
    },
    beforeDestroy: function () {
        this.stopGesture(true);
    },
    methods: {
        centerOf: function (geometry) {
            return {
                x: geometry.x + geometry.width / 2,
                y: geometry.y + geometry.height / 2
            };
        },
        // Rotates a screen delta into the unrotated coordinate system of the fixture
        toLocalDelta: function (deltaX, deltaY, rotation) {
            const angle = toRadians(-rotation);
            return {
                x: deltaX * Math.cos(angle) - deltaY * Math.sin(angle),
                y: deltaX * Math.sin(angle) + deltaY * Math.cos(angle)
            };
        },
        onPointerDown: function (event) {
            if (!this.editMode || this.gestureState) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            this.$emit('select', this.fixture.id);
            this.beginGesture('move', null, event);
        },
        onClick: function (event) {
            if (!this.isNavigable) {
                return;
            }

            event.stopPropagation();
            this.$emit('navigate', {
                up: this.fixture.targetRoomId,
                down: this.isStairs ? this.fixture.targetRoomDownId : null
            });
        },
        startResize: function (handle, event) {
            if (!this.editMode || this.gestureState) {
                return;
            }

            event.preventDefault();
            this.beginGesture(handle === 'rotate' ? 'rotate' : 'resize', handle, event);
        },
        beginGesture: function (mode, handle, event) {
            const element = event.currentTarget;
            const bounds = this.$el.getBoundingClientRect();

            if (element.setPointerCapture && event.pointerId !== undefined) {
                element.setPointerCapture(event.pointerId);
            }

            this.gestureState = {
                mode: mode,
                handle: handle,
                element: element,
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                center: {
                    x: bounds.left + bounds.width / 2,
                    y: bounds.top + bounds.height / 2
                },
                start: _.pick(this.fixture, ['x', 'y', 'width', 'height', 'rotation', 'label', 'type']),
                moved: false
            };

            window.addEventListener('pointermove', this.onGestureMove);
            window.addEventListener('pointerup', this.stopGesture);
            window.addEventListener('pointercancel', this.stopGesture);
        },
        onGestureMove: function (event) {
            if (!this.gestureState || event.pointerId !== this.gestureState.pointerId) {
                return;
            }

            // Pointer deltas are screen pixels, the plan coordinates are unscaled
            const deltaX = (event.clientX - this.gestureState.startX) / this.zoom;
            const deltaY = (event.clientY - this.gestureState.startY) / this.zoom;

            if (Math.abs(deltaX) > CLICK_THRESHOLD || Math.abs(deltaY) > CLICK_THRESHOLD) {
                this.gestureState.moved = true;
            }

            if (this.gestureState.mode === 'move') {
                this.draft = this.buildMovedGeometry(deltaX, deltaY);
                return;
            }

            if (this.gestureState.mode === 'rotate') {
                this.draft = this.buildRotatedGeometry(event);
                return;
            }

            this.draft = this.gestureState.start.type === 'line'
                ? this.buildResizedLineGeometry(deltaX, deltaY, !event.shiftKey)
                : this.buildResizedRectangleGeometry(deltaX, deltaY);
        },
        buildMovedGeometry: function (deltaX, deltaY) {
            const start = this.gestureState.start;

            // No room walls — objects are placed freely, also outside the room bounds
            return _.assign({}, start, {
                x: snapToGrid(start.x + deltaX),
                y: snapToGrid(start.y + deltaY)
            });
        },
        buildRotatedGeometry: function (event) {
            const start = this.gestureState.start;
            const center = this.gestureState.center;
            // The handle sits above the center, so pointing up must stay 0°
            let rotation = toDegrees(Math.atan2(event.clientY - center.y, event.clientX - center.x)) + 90;

            if (!event.shiftKey) {
                rotation = Math.round(rotation / ANGLE_SNAP_DEGREES) * ANGLE_SNAP_DEGREES;
            }

            return _.assign({}, start, { rotation: normalizeDegrees(rotation) });
        },
        buildResizedRectangleGeometry: function (deltaX, deltaY) {
            const start = this.gestureState.start;
            const handle = this.gestureState.handle;
            const localDelta = this.toLocalDelta(deltaX, deltaY, start.rotation);
            const growX = handle === 'nw' || handle === 'sw' ? -1 : 1;
            const growY = handle === 'nw' || handle === 'ne' ? -1 : 1;

            const width = Math.max(MINIMUM_SIZE, snapToGrid(start.width + growX * localDelta.x));
            const height = Math.max(MINIMUM_SIZE, snapToGrid(start.height + growY * localDelta.y));

            // The dragged corner moves by the effective delta, so the center moves by half of it
            const effectiveLocalX = growX * (width - start.width);
            const effectiveLocalY = growY * (height - start.height);
            const angle = toRadians(start.rotation);
            const center = this.centerOf(start);
            const centerX = center.x + (effectiveLocalX * Math.cos(angle) - effectiveLocalY * Math.sin(angle)) / 2;
            const centerY = center.y + (effectiveLocalX * Math.sin(angle) + effectiveLocalY * Math.cos(angle)) / 2;

            return _.assign({}, start, {
                width: width,
                height: height,
                x: Math.round(centerX - width / 2),
                y: Math.round(centerY - height / 2)
            });
        },
        buildResizedLineGeometry: function (deltaX, deltaY, snapAngle) {
            const start = this.gestureState.start;
            const center = this.centerOf(start);
            const angle = toRadians(start.rotation);
            const direction = { x: Math.cos(angle), y: Math.sin(angle) };
            const startPoint = {
                x: center.x - direction.x * start.width / 2,
                y: center.y - direction.y * start.width / 2
            };
            const endPoint = {
                x: center.x + direction.x * start.width / 2,
                y: center.y + direction.y * start.width / 2
            };

            const isEndHandle = this.gestureState.handle === 'end';
            const anchor = isEndHandle ? startPoint : endPoint;
            const moving = isEndHandle
                ? { x: endPoint.x + deltaX, y: endPoint.y + deltaY }
                : { x: startPoint.x + deltaX, y: startPoint.y + deltaY };

            const vectorX = moving.x - anchor.x;
            const vectorY = moving.y - anchor.y;
            let rotation = toDegrees(Math.atan2(vectorY, vectorX));

            if (snapAngle) {
                rotation = Math.round(rotation / ANGLE_SNAP_DEGREES) * ANGLE_SNAP_DEGREES;
            }

            // The dragged end lands on the anchor plus the new direction, the center sits halfway
            const length = Math.max(MINIMUM_LINE_LENGTH, snapToGrid(Math.sqrt(vectorX * vectorX + vectorY * vectorY)));
            const nextAngle = toRadians(rotation);
            const nextCenterX = anchor.x + Math.cos(nextAngle) * length / 2;
            const nextCenterY = anchor.y + Math.sin(nextAngle) * length / 2;

            return _.assign({}, start, {
                width: length,
                rotation: normalizeDegrees(isEndHandle ? rotation : rotation + 180),
                x: Math.round(nextCenterX - length / 2),
                y: Math.round(nextCenterY - start.height / 2)
            });
        },
        stopGesture: function (event) {
            if (!this.gestureState) {
                return;
            }

            // A second finger lifting must not end the gesture of the first one
            if (event && event !== true && event.pointerId !== this.gestureState.pointerId) {
                return;
            }

            const element = this.gestureState.element;
            const pointerId = this.gestureState.pointerId;
            const moved = this.gestureState.moved;
            const mode = this.gestureState.mode;
            const draft = this.draft;
            const silent = event === true;

            if (element && element.releasePointerCapture && pointerId !== undefined && element.hasPointerCapture(pointerId)) {
                element.releasePointerCapture(pointerId);
            }

            window.removeEventListener('pointermove', this.onGestureMove);
            window.removeEventListener('pointerup', this.stopGesture);
            window.removeEventListener('pointercancel', this.stopGesture);

            this.gestureState = null;

            if (silent || !moved || !draft) {
                this.draft = null;

                if (!silent && !moved && mode === 'move') {
                    this.registerTap();
                }

                return;
            }

            this.$emit('change', {
                id: this.fixture.id,
                geometry: _.pick(draft, ['x', 'y', 'width', 'height', 'rotation'])
            });
        },
        // preventDefault on pointerdown suppresses dblclick on touch, so taps are counted here
        registerTap: function () {
            const now = Date.now();

            if (this.lastTapAt && (now - this.lastTapAt) < DOUBLE_TAP_MILLISECONDS) {
                this.lastTapAt = 0;
                this.$emit('open-properties', this.fixture.id);
                return;
            }

            this.lastTapAt = now;
        }
    },
    watch: {
        fixture: function () {
            // The store answer replaces the draft once the change round-tripped
            if (!this.gestureState) {
                this.draft = null;
            }
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

$rotate-cursor: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M12 4.5A7.5 7.5 0 1 1 4.5 12' fill='none' stroke='%23ffffff' stroke-width='5' stroke-linecap='round'/%3E%3Cpath d='M10.5 1 16.5 4.5 10.5 8Z' fill='none' stroke='%23ffffff' stroke-width='5' stroke-linejoin='round'/%3E%3Cpath d='M12 4.5A7.5 7.5 0 1 1 4.5 12' fill='none' stroke='%23303133' stroke-width='2' stroke-linecap='round'/%3E%3Cpath d='M10.5 1 16.5 4.5 10.5 8Z' fill='%23303133'/%3E%3C/svg%3E") 12 12, grab

.floor-plan-fixture
    position: absolute
    z-index: 0
    touch-action: none
    // In view mode objects never swallow clicks meant for panning or tables
    pointer-events: none

    &__body
        width: 100%
        height: 100%
        display: flex
        align-items: center
        justify-content: center
        overflow: hidden

    &__label
        font-size: 0.75rem
        font-weight: 600
        color: #606266
        letter-spacing: 0.02em
        text-align: center
        padding: 0 6px
        white-space: nowrap
        text-overflow: ellipsis
        overflow: hidden
        user-select: none
        pointer-events: none
        position: relative
        z-index: 1

    &__shape
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%
        fill: #eef0f3
        stroke: #c0c4cc
        stroke-width: 1
        stroke-linejoin: round

    &--rectangle &__body,
    &--circle &__body
        background: #eef0f3
        border: 1px solid #c0c4cc
        border-radius: 4px

    &--circle &__body
        border-radius: 50%

    &--triangle &__body
        position: relative
        align-items: flex-end
        padding-bottom: 6px

    &__direction
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%
        fill: #909399
        stroke: #909399
        stroke-width: 2
        stroke-linecap: round

    &--stairs &__body,
    &--spiral-stairs &__body
        position: relative

    &--area-link &__body
        background: rgba($--color-primary, 0.06)
        border: 1px dashed $--color-primary
        border-radius: 6px

    &--area-link &__label
        color: $--color-primary

    &--stairs &__label,
    &--spiral-stairs &__label
        background: rgba(255, 255, 255, 0.85)
        border-radius: 3px
        padding: 1px 5px

    &--navigable
        pointer-events: auto
        cursor: pointer

        &:hover .floor-plan-fixture__shape
            stroke: $--color-primary
            fill: rgba($--color-primary, 0.08)

    &--line &__body
        background: #909399
        border-radius: 2px

    //- A thin line needs a taller grab area than its stroke
    &--line.floor-plan-fixture--editable::before
        content: ''
        position: absolute
        left: 0
        right: 0
        top: -10px
        bottom: -10px

    &--line &__label
        position: absolute
        bottom: 100%
        margin-bottom: 4px
        color: #909399

    &--editable
        pointer-events: auto
        cursor: move

    &--selected
        z-index: 6

        .floor-plan-fixture__body
            border-color: $--color-primary
            box-shadow: 0 0 0 2px rgba($--color-primary, 0.3)

        .floor-plan-fixture__shape
            stroke: $--color-primary
            stroke-width: 2

    &--selected.floor-plan-fixture--triangle .floor-plan-fixture__body
        box-shadow: none

    &--busy
        user-select: none

    &__handle
        position: absolute
        width: 12px
        height: 12px
        margin-left: -6px
        margin-top: -6px
        border: 1px solid $--color-primary
        border-radius: 50%
        background: #fff
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2)
        touch-action: none
        z-index: 7

        &--nw,
        &--se
            cursor: nwse-resize

        &--ne,
        &--sw
            cursor: nesw-resize

        &--start,
        &--end
            cursor: ew-resize

        &--rotate
            cursor: $rotate-cursor
            background: $--color-primary

            &::before
                content: ''
                position: absolute
                left: 50%
                top: 100%
                width: 1px
                height: 16px
                background: $--color-primary

    // Touch devices need a bigger target than the visible dot
    @media (pointer: coarse)
        &__handle
            width: 20px
            height: 20px
            margin-left: -10px
            margin-top: -10px
</style>
